import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import { MAP_STYLE, MAP_CONFIG } from "../custom/map_style"

// The Plans page's indoor-map viewer (decision-79): a Mapbox GL map of a
// site's traced floors. Ported from the tracer proof of concept's own viewer
// (mapbox-example.html) — rooms, walls, doors and labels for ONE level in
// Plan. 3D is a CUTAWAY at the selected floor: that level in full colour
// with its walls, the floors below as a ghost-grey massing for context, and
// the floors above hidden — otherwise a tower floor is always occluded by
// the slabs over it.
//
// Each level is fetched from its own GeoJSON endpoint
// (Sites::TracedLevelsController#show: lon/lat, already placed by the block's
// anchor) and merged into one source per layer, every feature tagged with its
// level, base_m and height_m — so switching level is a filter change, never a
// refetch. Plan | 3D is server-rendered into `mode` (RemembersViewMode,
// plans_view_mode); the level rail and the Publish forms are markup the
// server wrote, toggled here.
//
// The root wraps the WHOLE full-area page (TASK-271.3): the flush map, the
// floors panel (a w-80 column from lg, a bottom sheet below) and the cards
// laid over the map. The map target is a flex item — mapbox-gl.css forces
// .mapboxgl-map to position:relative, so it must never be absolute inset-0
// (docs/agent-conventions.md) — and the overlays are its siblings.
//
// The selection is an ELEVATION id (or "__building__"): every floor in the
// panel can be selected, drawn or not. A floor with no placed trace reveals
// its Selected block and draws nothing. A reload of the body frame (a trace
// finishing) re-mounts this controller; the selection survives it through
// `remembered`.
//
// Values:  token (Mapbox), levels ([{id, elevationId, name, block, draft, url}],
//          top first, drawable only), mode ("plan" | "3d"), selected (elevation
//          id | "__building__" | ""), explicit (the URL named the selection),
//          center ([lng, lat] or [] — where an empty map looks), asset (a
//          ProductInstance id the URL deep-linked with ?asset=; "" for none)
// Targets: map, title, draftBadge, level (panel row buttons), publish (one
//          hidden form per draft level, keyed by elevation id), selected
//          (one Selected block per floor), selectedEmpty, panel, sheetToggle,
//          sheetChevron, legendTitle, legendPlan, legend3d, legendWhole, hint,
//          error, errorText, colourBy (the three-pill door colouring toggle),
//          doorLegend (one list of legend rows per colouring, keyed by
//          data-mode), legendCount (a count span inside those rows, keyed by
//          data-count), levelsScroller (the panel's scrolling Levels region),
//          levelsEyebrow (its sticky heading), selectedAsset (one per floor's
//          Selected block, filled from the selected door's properties),
//          legendSelected (the "Selected asset" legend row)
//
// The selected ASSET (TASK-274.3): arriving with ?asset=, or clicking a
// door that has one, selects it. The door gets a ring (an orange line over a
// wider blue-800 casing, the sites map's selected-pin grammar applied to a
// line), its popup opens, the Selected block grows an Asset line, and the
// legend a row. Clicking empty map or "Clear" clears it; Escape closes the
// popup and keeps the selection.
//
// Doors carry their asset (TASK-273.4): the level JSON adds asset_id,
// asset_reference, product_name, qtag_reference, tagged, due_status,
// asset_url and (managers, untagged) assign_url to a door's properties.
// "Colour doors by" picks what the plan paints: Tag status (the default for a
// manager), Compliance (the default for a client team, via the colour-by
// value) or Door kind (the tracer's own reading). The pick is remembered per
// site in localStorage.

const LAYER_NAMES = ["rooms", "units", "walls", "fixtures", "door_swings", "doors", "windows", "labels"]
const SLAB = 0.3
const WALL_TOP_DEFAULT = 2.8

// Rooms carry `flat`; colour by flat number so neighbouring flats differ and
// the communal parts stay neutral.
const ROOM_COLOUR = ["case", ["has", "flat"],
  ["match", ["%", ["to-number", ["get", "flat"], 0], 6],
    0, "#cfe3d4", 1, "#f1dfc0", 2, "#d5dcf2", 3, "#f0d3d6", 4, "#d0e6ea", "#e6dcef"],
  ["match", ["get", "category"], "common", "#dfe5ea", "service", "#d3d9df", "#e3e8ec"]]
const WALL_COLOUR = ["match", ["get", "kind"], "structural", "#24303c", "#5c6975"]
// Doors the building's stack added (properties.provenance == "stack") read
// amber; the rest are the sheet's own.
const DOOR_COLOUR = ["case", ["==", ["get", "provenance"], "stack"], "#d99a1b",
  ["match", ["get", "kind"], "lift", "#55626f", "internal", "#e08a78", "#d2452d"]]
const GHOST_COLOUR = "#c3cad1"
// Door colourings (TASK-273.4). Tag status: tagged blue-600, untagged gray-400
// (doors are not site pins, so never the map-marker orange). Compliance: the
// status tokens, gray-500 for "no schedule". A door with no asset is the
// ghost, dashed grey, in both.
const COLOUR_MODES = ["tag", "compliance", "kind"]
const TAG_COLOUR = ["case", ["==", ["get", "tagged"], true], "#2563eb", "#9ca3af"]
const COMPLIANCE_COLOUR = ["match", ["get", "due_status"],
  "overdue", "#dc2626", "due_soon", "#d97706", "clear", "#16a34a", "#6b7280"]
const GHOST_DOOR_COLOUR = "#9ca3af"
// The selected asset's door: orange-600 over a blue-800 casing 3px wider.
const SELECTED_COLOUR = "#e05e0b"
const SELECTED_CASING_COLOUR = "#134d79"
const DOOR_LAYERS = ["indoor-doors", "indoor-doors-ghost"]
const ASSET_FIT_ZOOM = 20
// The rail's "Whole building" entry (3D only): every level drawn at once —
// the PoC's dollhouse reading, the big ground wings with the tower rising.
const WHOLE = "__building__"
// Below lg the panel is a bottom sheet (sites_map's breakpoint).
const SHEET_QUERY = "(max-width: 1023.98px)"
// The selection a body-frame reload should keep: { path, selection }.
let remembered = null

export default class extends Controller {
  static targets = ["map", "title", "draftBadge", "level", "publish", "selected", "selectedEmpty",
    "panel", "sheetToggle", "sheetChevron", "legendTitle", "legendPlan", "legend3d", "legendWhole",
    "hint", "error", "errorText", "colourBy", "doorLegend", "legendCount", "levelsScroller", "levelsEyebrow",
    "selectedAsset", "legendSelected"]
  static values = { token: String, levels: Array, mode: String, selected: String, explicit: Boolean, center: Array,
                    siteId: String, colourBy: String, asset: String }

  connect() {
    this.ready = false
    this.planLayers = []
    this.three = this.modeValue === "3d"
    this.colourMode = this.initialColourMode()
    this.selection = this.initialSelection()
    this.assetId = this.assetValue || null
    this.assetProps = null
    this.current = this.levelFor(this.selection)
    this.updateChrome()

    mapboxgl.accessToken = this.tokenValue
    try {
      this.map = new mapboxgl.Map({
        container: this.mapTarget,
        style: MAP_STYLE,
        // Standard draws its own 3D buildings; ours would fight them.
        config: { basemap: { ...MAP_CONFIG.basemap, show3dObjects: false } },
        center: this.hasSiteCentre() ? this.centerValue : [-2, 54],
        zoom: this.hasSiteCentre() ? 16 : 5,
        maxZoom: 24
      })
    } catch (error) {
      // No WebGL, or a browser mapbox-gl will not run in.
      this.fail(error)
      return
    }
    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")
    this.map.on("error", (event) => {
      if (!this.ready && !this.styleLoaded) this.fail(event.error)
    })
    this.map.on("style.load", () => { this.styleLoaded = true })
    this.map.on("load", () => this.load())

    // A map mounted in a hidden/0x0 container needs a resize when it shows.
    this.resizer = new ResizeObserver(() => this.map?.resize())
    this.resizer.observe(this.mapTarget)
  }

  disconnect() {
    if (this.selection) remembered = { path: window.location.pathname, selection: this.selection }
    this.resizer?.disconnect()
    if (this.orbitCleanup) this.orbitCleanup()
    this.popup?.remove()
    if (this.onEscape) document.removeEventListener("keydown", this.onEscape)
    this.map?.remove()
    this.map = null
  }

  // ------------------------------------------------------- door colouring --

  // The member's remembered pick for this site, else the server's default.
  initialColourMode() {
    try {
      const stored = window.localStorage.getItem(this.colourKey())
      if (COLOUR_MODES.includes(stored)) return stored
    } catch { /* storage blocked: the server default stands */ }
    return COLOUR_MODES.includes(this.colourByValue) ? this.colourByValue : "tag"
  }

  colourKey() {
    return `plans-viewer:colour-by:${this.siteIdValue}`
  }

  // A pill in the legend's "Colour doors by" toggle.
  pickColour(event) {
    const mode = event.currentTarget.dataset.mode
    if (!COLOUR_MODES.includes(mode) || mode === this.colourMode) return
    this.colourMode = mode
    try { window.localStorage.setItem(this.colourKey(), mode) } catch { /* not remembered */ }
    this.updateChrome()
    this.applyColourBy()
  }

  // Paint the door layers for the chosen colouring. Called after every
  // refresh(), because that resets the level filter on every plan layer.
  // Two layers: the assets' doors (painted by the colouring) and, outside
  // Door kind, a dashed grey ghost for doors with no asset — a dash pattern
  // cannot be data-driven, so it is its own layer.
  applyColourBy() {
    if (!this.ready || !this.map.getLayer("indoor-doors")) return
    const kind = this.colourMode === "kind"
    const hasAsset = ["has", "asset_id"]
    const paint = kind ? DOOR_COLOUR : (this.colourMode === "tag" ? TAG_COLOUR : COMPLIANCE_COLOUR)
    this.map.setPaintProperty("indoor-doors", "line-color", paint)
    this.map.setFilter("indoor-doors", kind ? this.onLevel() : this.onLevel(hasAsset))
    this.map.setFilter("indoor-doors-ghost", this.onLevel(["!", hasAsset]))
    this.map.setLayoutProperty("indoor-doors-ghost", "visibility", kind || this.three ? "none" : "visible")
  }

  // Counts for the legend, from the doors of the level on screen (every level
  // for the whole building): tagged / untagged / overdue ... / no asset.
  updateLegend() {
    if (!this.hasLegendCountTarget || !this.doorsByLevel) return
    const levels = this.current === WHOLE ? Object.keys(this.doorsByLevel) : [this.current]
    const counts = { tagged: 0, untagged: 0, overdue: 0, due_soon: 0, clear: 0, none: 0, no_asset: 0 }
    for (const level of levels) {
      for (const props of this.doorsByLevel[level] || []) {
        if (!props.asset_id) { counts.no_asset += 1; continue }
        counts[props.tagged ? "tagged" : "untagged"] += 1
        counts[props.due_status in counts ? props.due_status : "none"] += 1
      }
    }
    for (const node of this.legendCountTargets) node.textContent = String(counts[node.dataset.count] ?? 0)
  }

  // The server's choice, unless the body frame was just reloaded under a
  // selection the member made (and the URL did not name one).
  initialSelection() {
    const kept = remembered && remembered.path === window.location.pathname ? remembered.selection : null
    if (!this.explicitValue && kept && this.hasRow(kept) && (kept !== WHOLE || this.three)) return kept
    return this.selectedValue
  }

  hasRow(id) {
    return this.levelTargets.some((button) => button.dataset.levelId === id)
  }

  hasSiteCentre() {
    return this.centerValue.length === 2
  }

  // The traced level drawn for a selection: the whole building, one level,
  // or null (a floor with nothing placed yet).
  levelFor(selection) {
    if (selection === WHOLE) return this.levelsValue.length > 1 ? WHOLE : (this.levelsValue[0]?.id ?? null)
    return this.levelsValue.find((entry) => entry.elevationId === selection)?.id ?? null
  }

  // Panel row button.
  select(event) {
    const button = event.currentTarget
    if (button.disabled) return
    this.selection = button.dataset.levelId
    this.current = this.selection === WHOLE ? WHOLE : (button.dataset.tracedLevelId || null)
    this.updateChrome()
    this.refresh()
    if (this.three && this.ready) this.retarget3d()
    if (this.sheetMode()) this.setSheet(false)
  }

  async load() {
    if (this.levelsValue.length === 0) {
      // Nothing drawable: the map sits on the site and the page says why.
      this.resizeSoon()
      return
    }
    const loaded = (await Promise.all(this.levelsValue.map((level) => this.fetchLevel(level)))).filter(Boolean)
    if (loaded.length === 0) {
      this.fail(new Error("None of this site's levels could be placed on the map."))
      return
    }
    // The panel may list a level that failed to load; keep the drawing real.
    if (this.current === WHOLE) {
      if (loaded.length < 2) this.current = loaded[0].meta.id
    } else if (this.current && !loaded.some((entry) => entry.meta.id === this.current)) {
      this.current = null
    }
    this.updateChrome()

    const collections = Object.fromEntries(LAYER_NAMES.map((name) => [name, []]))
    this.baseByLevel = {}
    this.boundsByLevel = {}
    this.doorsByLevel = {}
    this.doorFeatures = {}
    for (const { meta, payload } of loaded) {
      this.doorFeatures[meta.id] = []
      this.doorsByLevel[meta.id] = (payload.layers?.doors?.features || []).map((feature) => feature.properties || {})
      this.baseByLevel[meta.id] = payload.base_m || 0
      for (const name of LAYER_NAMES) {
        for (const feature of payload.layers?.[name]?.features || []) {
          const merged = {
            ...feature,
            properties: { ...feature.properties, level: meta.id, base_m: payload.base_m || 0, height_m: payload.height_m ?? null }
          }
          collections[name].push(merged)
          if (name === "doors") this.doorFeatures[meta.id].push(merged)
          if (name === "rooms" || name === "walls") {
            this.boundsByLevel[meta.id] ||= new mapboxgl.LngLatBounds()
            this.extendBounds(this.boundsByLevel[meta.id], feature.geometry.coordinates)
          }
        }
      }
    }

    for (const name of LAYER_NAMES) {
      this.map.addSource(name, {
        type: "geojson",
        data: { type: "FeatureCollection", features: collections[name] },
        maxzoom: 22,
        tolerance: 0.1,
        generateId: name === "rooms"
      })
    }
    this.addPlanLayers()
    this.addBuildingLayers()
    this.bindInteractions()
    this.ready = true
    this.refresh()
    this.updateLegend()
    if (this.three) {
      this.enableOrbit()
      this.frame3d(0)
    } else {
      this.fitToLevels(collections)
    }
    if (this.assetId) this.selectAsset(this.assetId, { duration: 0 })
  }

  async fetchLevel(meta) {
    try {
      const response = await fetch(meta.url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!response.ok) return null
      return { meta, payload: await response.json() }
    } catch {
      return null
    }
  }

  onLevel(extra) {
    const here = ["==", ["get", "level"], this.current]
    return extra ? ["all", here, extra] : here
  }

  plan(layer, extra) {
    this.planLayers.push([layer.id, extra || null])
    layer.filter = this.onLevel(extra)
    this.map.addLayer(layer)
  }

  addPlanLayers() {
    this.plan({ id: "indoor-rooms", type: "fill", source: "rooms", minzoom: 16,
      paint: { "fill-color": ROOM_COLOUR, "fill-opacity": ["case", ["boolean", ["feature-state", "hover"], false], 0.75, 1] } })
    this.plan({ id: "indoor-room-selected", type: "line", source: "rooms",
      paint: { "line-color": "#0f6fb5", "line-width": ["case", ["boolean", ["feature-state", "selected"], false], 3, 0] } })
    this.plan({ id: "indoor-fixtures", type: "line", source: "fixtures", minzoom: 19,
      paint: { "line-color": "#8794a1", "line-width": ["interpolate", ["linear"], ["zoom"], 19, 0.4, 23, 1.2] } },
    ["==", ["get", "style"], "solid"])
    this.plan({ id: "indoor-walls", type: "fill", source: "walls", minzoom: 16, paint: { "fill-color": WALL_COLOUR } })
    this.plan({ id: "indoor-door-swings", type: "line", source: "door_swings", minzoom: 20,
      paint: { "line-color": "#d2452d", "line-width": 0.8, "line-opacity": 0.7 } })
    this.plan({ id: "indoor-doors", type: "line", source: "doors", minzoom: 18, layout: { "line-cap": "round" },
      paint: {
        "line-color": DOOR_COLOUR,
        "line-width": ["interpolate", ["linear"], ["zoom"], 18, 1.2, 23, 6]
      } })
    // Doors with no asset (internal doors, or none made yet), outside the Door
    // kind colouring: a dashed ghost. Filtered by applyColourBy().
    this.plan({ id: "indoor-doors-ghost", type: "line", source: "doors", minzoom: 18,
      paint: {
        "line-color": GHOST_DOOR_COLOUR, "line-dasharray": [2, 2],
        "line-width": ["interpolate", ["linear"], ["zoom"], 18, 1, 23, 4]
      } })
    // The selected asset's ring (TASK-274.3). Not a plan() layer: refresh()
    // would reset its filter to the level alone; applyAssetRing() owns it.
    this.map.addLayer({ id: "indoor-doors-selected-casing", type: "line", source: "doors",
      layout: { "line-cap": "round" }, filter: ["==", ["get", "asset_id"], ""],
      paint: { "line-color": SELECTED_CASING_COLOUR,
        "line-width": ["interpolate", ["linear"], ["zoom"], 14, 4.5, 18, 5.5, 23, 9] } })
    this.map.addLayer({ id: "indoor-doors-selected", type: "line", source: "doors",
      layout: { "line-cap": "round" }, filter: ["==", ["get", "asset_id"], ""],
      paint: { "line-color": SELECTED_COLOUR,
        "line-width": ["interpolate", ["linear"], ["zoom"], 14, 1.5, 18, 2.5, 23, 6] } })
    this.plan({ id: "indoor-room-labels", type: "symbol", source: "labels", minzoom: 19.5,
      layout: { "text-field": ["get", "text"], "text-size": ["interpolate", ["linear"], ["zoom"], 19.5, 10, 23, 16] },
      paint: { "text-color": "#1c2733", "text-halo-color": "#fff", "text-halo-width": 1.5 } },
    ["==", ["get", "kind"], "room"])
    this.plan({ id: "indoor-unit-labels", type: "symbol", source: "labels", minzoom: 17.5, maxzoom: 19.5,
      layout: { "text-field": ["get", "text"], "text-size": 12 },
      paint: { "text-color": "#1c2733", "text-halo-color": "#fff", "text-halo-width": 1.5 } },
    ["==", ["get", "kind"], "unit"])
    this.plan({ id: "indoor-door-refs", type: "symbol", source: "labels", minzoom: 21.5,
      layout: { "text-field": ["get", "text"], "text-size": 10, "text-offset": [0, 1] },
      paint: { "text-color": "#b53a25", "text-halo-color": "#fff", "text-halo-width": 1.2 } },
    ["==", ["get", "kind"], "door"])
  }

  // The cutaway: the selected level in full colour with its walls, the
  // levels BELOW as a ghost-grey massing (slab + walls, so the building has
  // volume rather than floating plates), the levels ABOVE not drawn at all.
  addBuildingLayers() {
    const top = ["+", ["get", "base_m"], ["coalesce", ["get", "height_m"], WALL_TOP_DEFAULT]]
    this.map.addLayer({ id: "b-slabs-other", type: "fill-extrusion", source: "rooms", layout: { visibility: "none" },
      paint: { "fill-extrusion-color": GHOST_COLOUR, "fill-extrusion-base": ["get", "base_m"],
        "fill-extrusion-height": ["+", ["get", "base_m"], SLAB], "fill-extrusion-opacity": 0.45 } })
    this.map.addLayer({ id: "b-walls-ghost", type: "fill-extrusion", source: "walls", layout: { visibility: "none" },
      paint: { "fill-extrusion-color": GHOST_COLOUR, "fill-extrusion-base": ["+", ["get", "base_m"], SLAB],
        "fill-extrusion-height": top, "fill-extrusion-opacity": 0.25 } })
    this.map.addLayer({ id: "b-slabs-level", type: "fill-extrusion", source: "rooms", layout: { visibility: "none" },
      paint: { "fill-extrusion-color": ROOM_COLOUR, "fill-extrusion-base": ["get", "base_m"],
        "fill-extrusion-height": ["+", ["get", "base_m"], SLAB], "fill-extrusion-opacity": 1 } })
    this.map.addLayer({ id: "b-walls", type: "fill-extrusion", source: "walls", layout: { visibility: "none" },
      paint: { "fill-extrusion-color": WALL_COLOUR, "fill-extrusion-base": ["+", ["get", "base_m"], SLAB],
        "fill-extrusion-height": top, "fill-extrusion-opacity": 0.8 } })
  }

  refresh() {
    if (!this.ready) return
    for (const [id, extra] of this.planLayers) {
      this.map.setFilter(id, this.onLevel(extra))
      this.map.setLayoutProperty(id, "visibility", this.three ? "none" : "visible")
    }
    if (this.current === WHOLE) {
      // Dollhouse: every level a full-colour slab, no ghosts, no walls.
      this.map.setFilter("b-slabs-level", ["has", "level"])
      for (const id of ["b-slabs-other", "b-walls-ghost", "b-walls"]) {
        this.map.setFilter(id, ["==", ["get", "level"], WHOLE])
      }
    } else if (!this.current) {
      // A floor with nothing drawn: no selected level, the others stay as
      // the ghost massing for context.
      this.map.setFilter("b-slabs-level", ["==", ["get", "level"], ""])
      this.map.setFilter("b-walls", ["==", ["get", "level"], ""])
      this.map.setFilter("b-slabs-other", ["has", "level"])
      this.map.setFilter("b-walls-ghost", ["has", "level"])
    } else {
      const base = this.baseByLevel?.[this.current] ?? 0
      const below = ["all", ["!=", ["get", "level"], this.current], ["<", ["get", "base_m"], base]]
      this.map.setFilter("b-slabs-level", ["==", ["get", "level"], this.current])
      this.map.setFilter("b-slabs-other", below)
      this.map.setFilter("b-walls-ghost", below)
      this.map.setFilter("b-walls", ["==", ["get", "level"], this.current])
    }
    for (const id of ["b-slabs-level", "b-slabs-other", "b-walls-ghost", "b-walls"]) {
      this.map.setLayoutProperty(id, "visibility", this.three ? "visible" : "none")
    }
    this.applyColourBy()
    this.applyAssetRing()
    this.updateLegend()
  }

  bindInteractions() {
    let hovered = null
    let selected = null
    this.map.on("mousemove", "indoor-rooms", (event) => {
      this.map.getCanvas().style.cursor = "pointer"
      const id = event.features[0].id
      if (hovered !== null && hovered !== id) this.map.setFeatureState({ source: "rooms", id: hovered }, { hover: false })
      hovered = id
      this.map.setFeatureState({ source: "rooms", id }, { hover: true })
    })
    this.map.on("mouseleave", "indoor-rooms", () => {
      this.map.getCanvas().style.cursor = ""
      if (hovered !== null) this.map.setFeatureState({ source: "rooms", id: hovered }, { hover: false })
      hovered = null
    })
    this.map.on("click", "indoor-rooms", (event) => {
      const feature = event.features[0]
      if (selected !== null) this.map.setFeatureState({ source: "rooms", id: selected }, { selected: false })
      selected = feature.id
      this.map.setFeatureState({ source: "rooms", id: selected }, { selected: true })
      const p = feature.properties
      this.showPopup(event.lngLat, [
        [p.name || "Room", true],
        [[p.flat ? `Flat ${p.flat}` : null, p.area_m2 ? `${p.area_m2} m²` : null].filter(Boolean).join(" · ")]
      ])
    })
    for (const layer of DOOR_LAYERS) {
      this.map.on("mouseenter", layer, () => { this.map.getCanvas().style.cursor = "pointer" })
      this.map.on("mouseleave", layer, () => { this.map.getCanvas().style.cursor = "" })
      this.map.on("click", layer, (event) => this.clickDoor(event))
    }
    // Empty map clears the asset selection (a click on a door is the door
    // handler's business).
    this.map.on("click", (event) => {
      if (!this.assetId) return
      if (this.map.queryRenderedFeatures(event.point, { layers: DOOR_LAYERS }).length) return
      this.clearAsset()
    })
    // In 3D, clicking another level's slab — or any slab in the whole-
    // building view — selects that level.
    const pick = (event) => {
      const level = event.features[0].properties.level
      if (level === this.current) return
      this.current = level
      this.selection = this.levelsValue.find((entry) => entry.id === level)?.elevationId || this.selection
      this.updateChrome()
      this.refresh()
      this.retarget3d()
    }
    this.map.on("click", "b-slabs-other", pick)
    this.map.on("click", "b-slabs-level", (event) => { if (this.current === WHOLE) pick(event) })
  }

  // ------------------------------------------------------ selected asset --

  // The stored feature for an asset id, across every loaded level.
  findDoor(assetId) {
    for (const [level, features] of Object.entries(this.doorFeatures || {})) {
      const feature = features.find((candidate) => candidate.properties?.asset_id === assetId)
      if (feature) return { feature, level }
    }
    return null
  }

  // A click on a door: one with an asset selects it (ring, Asset line,
  // popup); one without clears any selection and just says what it is.
  clickDoor(event) {
    const clicked = event.features[0].properties
    if (!clicked.asset_id) {
      this.dropAsset()
      this.showDoorPopup(event.lngLat, clicked)
      return
    }
    const props = this.findDoor(clicked.asset_id)?.feature.properties || clicked
    this.assetId = clicked.asset_id
    this.assetProps = props
    this.applyAssetRing()
    this.updateAssetChrome()
    this.showDoorPopup(event.lngLat, props, { asset: true })
  }

  // Select an asset's door: its level first (the cutaway in 3D), then frame
  // the door (80px padding, never closer than zoom 20 — a 1 m door must not
  // fly to the void), ring it and open its popup. An id with no door on a
  // loaded level selects nothing.
  selectAsset(assetId, { duration = 600 } = {}) {
    if (!this.ready) return
    const found = this.findDoor(assetId)
    if (!found) {
      this.assetId = null
      this.assetProps = null
      this.applyAssetRing()
      this.updateAssetChrome()
      return
    }
    const { feature, level } = found
    this.assetId = assetId
    this.assetProps = feature.properties
    const entry = this.levelsValue.find((candidate) => candidate.id === level)
    if (entry && (this.selection !== entry.elevationId || this.current !== level)) {
      this.selection = entry.elevationId
      this.current = level
      this.updateChrome()
      this.refresh()
    }
    this.applyAssetRing()
    this.updateAssetChrome()

    const bounds = new mapboxgl.LngLatBounds()
    this.extendBounds(bounds, feature.geometry.coordinates)
    if (bounds.isEmpty()) return
    if (this.three) {
      const cam = this.map.cameraForBounds(bounds, { padding: 80, maxZoom: ASSET_FIT_ZOOM })
      if (cam) {
        const pitch = this.map.getPitch()
        const base = this.baseByLevel?.[level] ?? 0
        this.map.easeTo({ center: cam.center, zoom: cam.zoom, pitch, bearing: this.map.getBearing(),
                          offset: [0, this.riseOffset(pitch, cam.zoom, cam.center.lat, base)], duration })
      }
    } else {
      this.map.fitBounds(bounds, { padding: 80, maxZoom: ASSET_FIT_ZOOM, duration })
    }
    this.showDoorPopup(bounds.getCenter(), feature.properties, { asset: true })
  }

  // The Selected block's "Clear" and the empty-map click.
  clearAsset() {
    if (!this.assetId) return
    this.dropAsset()
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has("asset")) {
        url.searchParams.delete("asset")
        window.history.replaceState(window.history.state, "", url)
      }
    } catch { /* the URL stays as it was */ }
  }

  dropAsset() {
    this.assetId = null
    this.assetProps = null
    if (this.popup && this.popup === this.assetPopup) this.popup.remove()
    this.assetPopup = null
    this.applyAssetRing()
    this.updateAssetChrome()
  }

  // Ring the selected door on the plan: the line over its casing. Plan view
  // only — doors are not drawn in 3D.
  applyAssetRing() {
    if (!this.ready || !this.map.getLayer("indoor-doors-selected")) return
    const filter = this.assetId
      ? ["all", ["==", ["get", "level"], this.current], ["==", ["get", "asset_id"], this.assetId]]
      : ["==", ["get", "asset_id"], ""]
    for (const id of ["indoor-doors-selected-casing", "indoor-doors-selected"]) {
      this.map.setFilter(id, filter)
      this.map.setLayoutProperty(id, "visibility", this.three ? "none" : "visible")
    }
  }

  // The Selected block's Asset line and the legend row follow the selection.
  updateAssetChrome() {
    const props = this.assetId ? this.assetProps : null
    for (const host of this.selectedAssetTargets) {
      const mine = host.closest("[data-level-id]")?.dataset.levelId === this.selection
      host.replaceChildren()
      host.hidden = !(props && mine)
      if (props && mine) this.fillAssetLine(host, props)
    }
    if (this.hasLegendSelectedTarget) this.legendSelectedTarget.hidden = !this.assetId
  }

  // The Asset line: eyebrow + Clear, "<ref> · <product>", "<where> · <width>
  // · <tag chip>", the two links. Same properties, same helpers as the popup.
  fillAssetLine(host, p) {
    const head = el("div", "flex items-baseline justify-between")
    head.appendChild(el("span", "block text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500", "Asset"))
    const clear = el("button", "text-xs font-medium text-blue-600 hover:text-blue-800", "\u2715 Clear")
    clear.type = "button"
    clear.setAttribute("aria-label", "Clear selected asset")
    clear.dataset.action = "click->plans-viewer#clearAsset"
    head.appendChild(clear)
    host.appendChild(head)

    const title = el("p", "mt-1 truncate text-sm text-gray-900")
    title.appendChild(el("span", "font-mono font-semibold", p.asset_reference || p.ref || "Opening"))
    title.appendChild(document.createTextNode(` \u00b7 ${p.product_name || "Asset"}`))
    host.appendChild(title)

    const line = el("p", "mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-600")
    const text = doorLine(p)
    if (text) line.appendChild(el("span", "", `${text} \u00b7`))
    line.appendChild(tagChip(p))
    host.appendChild(line)

    const links = assetLinks(p)
    if (links) host.appendChild(links)
  }

  // The door's popup (TASK-273.4): bold "<ref> · <product>" (or "· Internal
  // door" with no asset), "Flat 56 · 830 mm leaf", the tag chip, and the
  // links — Open asset, and Assign QTAG, which opens the same modal an asset
  // row does (the data attributes the modal controller reads, set here
  // because the popup is built as DOM). Focusable, Escape closes it.
  showDoorPopup(lngLat, p, { asset = false } = {}) {
    this.popup?.remove()
    const node = document.createElement("div")
    node.tabIndex = -1
    node.className = "space-y-1 py-1 font-normal outline-none"
    const hasAsset = Boolean(p.asset_id)
    const reference = p.asset_reference || p.ref || "Opening"
    node.appendChild(el("strong", "block text-sm font-semibold text-gray-900",
      `${reference} \u00b7 ${hasAsset ? (p.product_name || "Asset") : "Internal door"}`))
    const line = [doorLocation(p), p.width_mm ? `${p.width_mm} mm leaf` : null].filter(Boolean).join(" \u00b7 ")
    if (line) node.appendChild(el("div", "text-xs text-gray-600", line))
    if (hasAsset) {
      node.appendChild(tagChip(p))
      const links = assetLinks(p)
      if (links) node.appendChild(links)
    }
    this.popup = new mapboxgl.Popup({ className: "map-popup-flat", closeButton: false, maxWidth: "260px", offset: 8 })
      .setLngLat(lngLat).setDOMContent(node).addTo(this.map)
    this.assetPopup = asset ? this.popup : null
    node.focus({ preventScroll: true })
    if (!this.onEscape) {
      this.onEscape = (event) => { if (event.key === "Escape") this.popup?.remove() }
      document.addEventListener("keydown", this.onEscape)
    }
  }

  // Popup content is built as DOM text, never HTML — room names come from
  // the drawing's own labels.
  showPopup(lngLat, lines) {
    this.popup?.remove()
    const node = document.createElement("div")
    for (const [text, bold] of lines) {
      if (!text) continue
      const line = document.createElement(bold ? "strong" : "div")
      line.textContent = text
      node.appendChild(line)
    }
    this.popup = new mapboxgl.Popup().setLngLat(lngLat).setDOMContent(node).addTo(this.map)
  }

  // Map gestures are wrong for a model: left-drag slides the map out from
  // under the building, and rotation hides behind right-drag, pivoting on
  // the screen rather than the floor. In 3D the mouse works like every 3D
  // model viewer instead (the turntable convention): drag orbits the
  // selected floor — horizontal spins the bearing, vertical tilts the pitch
  // — right-drag (or shift-drag) moves, scroll zooms, double-click
  // re-frames. Two-finger touch still pinches through Mapbox's own handler;
  // a single finger orbits. Plan view keeps the normal map gestures.
  enableOrbit() {
    this.map.dragPan.disable()
    this.map.dragRotate.disable()
    this.map.doubleClickZoom.disable()
    this.map.on("dblclick", (event) => { event.preventDefault(); this.frame3d() })

    const canvas = this.map.getCanvas()
    canvas.style.touchAction = "none"
    this.pointers = new Set()
    const down = (event) => this.orbitStart(event)
    const move = (event) => this.orbitMove(event)
    const up = (event) => this.orbitEnd(event)
    const menu = (event) => event.preventDefault()
    canvas.addEventListener("pointerdown", down)
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
    window.addEventListener("pointercancel", up)
    canvas.addEventListener("contextmenu", menu)
    this.orbitCleanup = () => {
      canvas.removeEventListener("pointerdown", down)
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
      window.removeEventListener("pointercancel", up)
      canvas.removeEventListener("contextmenu", menu)
    }
  }

  orbitStart(event) {
    this.pointers.add(event.pointerId)
    // A second finger means pinch — stand aside for Mapbox's touch handler.
    if (this.pointers.size > 1) { this.gesture = null; return }
    if (event.pointerType === "mouse" && event.button !== 0 && event.button !== 2) return
    this.gesture = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      mode: event.button === 2 || event.shiftKey ? "pan" : "orbit",
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch()
    }
  }

  orbitMove(event) {
    if (!this.gesture || event.pointerId !== this.gesture.id) return
    const dx = event.clientX - this.gesture.x
    const dy = event.clientY - this.gesture.y
    this.gesture.x = event.clientX
    this.gesture.y = event.clientY
    if (this.gesture.mode === "pan") {
      this.map.panBy([-dx, -dy], { duration: 0 })
      return
    }
    this.gesture.bearing -= dx * 0.4
    this.gesture.pitch = Math.min(78, Math.max(0, this.gesture.pitch - dy * 0.25))
    const subject = this.viewSubject()
    const options = { bearing: this.gesture.bearing, pitch: this.gesture.pitch }
    if (subject) {
      // Pivot on the subject itself (with its altitude compensated), so an
      // upper storey — or the whole stack — spins in place instead of
      // sweeping an arc.
      const centre = subject.bounds.getCenter()
      options.center = centre
      options.offset = [0, this.riseOffset(this.gesture.pitch, this.map.getZoom(), centre.lat, subject.base)]
    }
    this.map.jumpTo(options)
  }

  orbitEnd(event) {
    this.pointers.delete(event.pointerId)
    if (this.gesture && event.pointerId === this.gesture.id) this.gesture = null
  }

  // Where the camera pivots and how high the subject sits: the selected
  // floor's centre and base, or for the whole building its union footprint
  // and half the stack's height.
  viewSubject() {
    if (this.current === WHOLE) {
      const union = new mapboxgl.LngLatBounds()
      for (const bounds of Object.values(this.boundsByLevel || {})) union.extend(bounds)
      if (union.isEmpty()) return null
      const bases = Object.values(this.baseByLevel || {})
      return { bounds: union, base: bases.length ? Math.max(...bases) / 2 : 0 }
    }
    const bounds = this.boundsByLevel?.[this.current]
    if (!bounds || bounds.isEmpty()) return null
    return { bounds, base: this.baseByLevel?.[this.current] ?? 0 }
  }

  // How far (screen px) the subject's height lifts it above the ground
  // plane the camera targets — the vertical offset that keeps it centred.
  riseOffset(pitch, zoom, lat, base) {
    const metresPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)
    return ((base / metresPerPixel) * Math.sin((pitch * Math.PI) / 180)) / 2
  }

  extendBounds(bounds, coords) {
    if (typeof coords[0] === "number") bounds.extend(coords)
    else coords.forEach((inner) => this.extendBounds(bounds, inner))
  }

  fitToLevels(collections) {
    const bounds = new mapboxgl.LngLatBounds()
    for (const feature of [...collections.rooms, ...collections.walls]) {
      this.extendBounds(bounds, feature.geometry.coordinates)
    }
    if (bounds.isEmpty()) return
    this.map.fitBounds(bounds, { padding: 60, maxZoom: 20, duration: 0 })
  }

  // Frame the SELECTED floor. cameraForBounds works on the ground plane, but
  // the floor renders base_m above it, so under pitch the slab drifts up and
  // off-centre — nudge the screen anchor down by roughly the slab's on-screen
  // rise so a 14th floor is as centred as a ground floor.
  // Switching floors keeps YOUR view: bearing, tilt and zoom stay where you
  // put them, and only the camera's subject glides to the new floor's centre
  // at its height — flicking up the tower reads as riding a lift, not a
  // teleport. Double-click (and first load) still re-frame fully.
  retarget3d(duration = 400) {
    const subject = this.viewSubject()
    if (!subject) return
    const centre = subject.bounds.getCenter()
    const pitch = this.map.getPitch()
    this.map.easeTo({ center: centre, pitch, bearing: this.map.getBearing(),
                      offset: [0, this.riseOffset(pitch, this.map.getZoom(), centre.lat, subject.base)], duration })
  }

  frame3d(duration = 600) {
    const subject = this.viewSubject()
    if (!subject) return
    const cam = this.map.cameraForBounds(subject.bounds, { padding: 90, maxZoom: 20 })
    if (!cam) return
    // Enough tilt to read as 3D, enough top-down to see INTO the rooms past
    // the extruded walls. The whole building leans back a little further.
    const pitch = this.current === WHOLE ? 55 : 45
    const zoom = cam.zoom - 0.4
    this.map.easeTo({ center: cam.center, zoom, pitch, bearing: 0,
                      offset: [0, this.riseOffset(pitch, zoom, cam.center.lat, subject.base)], duration })
  }

  // Panel rows, the Selected blocks, the title card (name, Draft badge,
  // Publish) and the legend all follow the selection.
  updateChrome() {
    const row = this.levelTargets.find((button) => button.dataset.levelId === this.selection)
    const whole = this.selection === WHOLE
    for (const button of this.levelTargets) {
      const active = button.dataset.levelId === this.selection
      button.dataset.active = String(active)
      button.setAttribute("aria-pressed", String(active))
    }
    for (const block of this.selectedTargets) block.hidden = block.dataset.levelId !== this.selection
    if (this.hasSelectedEmptyTarget) this.selectedEmptyTarget.hidden = Boolean(row) && !whole
    for (const form of this.publishTargets) form.hidden = form.dataset.levelId !== this.selection
    const name = whole ? "Whole building" : (row?.dataset.levelName || "")
    if (this.hasTitleTarget) this.titleTarget.textContent = name
    if (this.hasLegendTitleTarget) this.legendTitleTarget.textContent = name || "Legend"
    if (this.hasDraftBadgeTarget) this.draftBadgeTarget.hidden = whole || row?.dataset.draft !== "true"
    if (this.hasLegendPlanTarget) this.legendPlanTarget.hidden = this.three
    for (const list of this.doorLegendTargets) list.hidden = list.dataset.mode !== this.colourMode
    for (const pill of this.colourByTargets) pill.setAttribute("aria-pressed", String(pill.dataset.mode === this.colourMode))
    this.updateLegend()
    if (this.hasLegend3dTarget) this.legend3dTarget.hidden = !this.three || whole
    if (this.hasLegendWholeTarget) this.legendWholeTarget.hidden = !whole
    if (this.hasHintTarget) this.hintTarget.hidden = !this.three
    this.updateAssetChrome()
    this.scrollActiveRow()
  }

  // Keep the selected row visible in the Levels scroller. Done by hand on
  // the scroller's own scrollTop — scrollIntoView can also move the page —
  // and a no-op where the scroller does not scroll (the bottom sheet).
  scrollActiveRow() {
    if (!this.hasLevelsScrollerTarget) return
    const scroller = this.levelsScrollerTarget
    if (scroller.scrollHeight <= scroller.clientHeight) return
    const row = this.levelTargets.find((button) => button.dataset.levelId === this.selection)
    if (!row) return
    const item = row.closest("li") || row
    const box = scroller.getBoundingClientRect()
    const rect = item.getBoundingClientRect()
    const sticky = this.hasLevelsEyebrowTarget ? this.levelsEyebrowTarget.offsetHeight : 0
    if (rect.top < box.top + sticky) scroller.scrollTop -= box.top + sticky - rect.top
    else if (rect.bottom > box.bottom) scroller.scrollTop += rect.bottom - box.bottom
  }

  // ---------------------------------------------------------------- sheet --

  sheetMode() {
    return window.matchMedia(SHEET_QUERY).matches
  }

  toggleSheet() {
    this.setSheet(!this.panelTarget.hasAttribute("data-expanded"))
  }

  setSheet(expanded) {
    if (!this.hasPanelTarget) return
    this.panelTarget.toggleAttribute("data-expanded", expanded)
    if (!expanded) this.panelTarget.scrollTop = 0
    if (this.hasSheetToggleTarget) this.sheetToggleTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
    if (this.hasSheetChevronTarget) this.sheetChevronTarget.classList.toggle("rotate-180", expanded)
  }

  // The sheet overlays the map rather than resizing it, but resize() is cheap
  // and keeps the canvas honest after any layout change.
  sheetSettled(event) {
    if (event.target !== this.panelTarget) return
    this.resizeSoon()
  }

  resizeSoon() {
    requestAnimationFrame(() => this.map?.resize())
  }

  fail(error) {
    console.error("[plans-viewer]", error)
    if (this.hasErrorTarget) this.errorTarget.hidden = false
    if (this.hasErrorTextTarget && error?.message) this.errorTextTarget.textContent = error.message
  }
}

// ------------------------------------------------------- DOM helpers --

function el(tag, className, text) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text) node.textContent = text
  return node
}

// "Flat 56", or the rooms the door sits between. Mapbox hands array
// properties back as JSON text; the stored features carry real arrays.
function doorLocation(p) {
  if (p.flat) return `Flat ${p.flat}`
  let between = p.between
  if (typeof between === "string") { try { between = JSON.parse(between) } catch { between = [] } }
  return [...new Set(Array.isArray(between) ? between : [])].join(" \u2013 ")
}

function doorLine(p) {
  return [doorLocation(p), p.width_mm ? `${p.width_mm} mm leaf` : null].filter(Boolean).join(" \u00b7 ")
}

// The tag badge, B's look: a small mono chip, amber when untagged.
function tagChip(p) {
  return p.tagged
    ? el("span", "inline-block rounded bg-blue-50 px-1.5 py-0.5 font-mono text-xs text-blue-800", p.qtag_reference)
    : el("span", "inline-block rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800", "Untagged")
}

// Open asset, and Assign QTAG opening the same modal an asset row does.
function assetLinks(p) {
  if (!p.asset_url) return null
  const links = el("div", "flex items-center gap-3 pt-1 text-xs font-medium")
  const open = el("a", "text-blue-600 hover:text-blue-800", "Open asset")
  open.href = p.asset_url
  open.dataset.turboFrame = "_top"
  links.appendChild(open)
  if (p.assign_url) {
    const assign = el("button", "text-blue-600 hover:text-blue-800", "Assign QTAG")
    assign.type = "button"
    assign.dataset.action = "click->modal#open:prevent"
    assign.dataset.modalTurboFrameSrcValue = p.assign_url
    assign.dataset.modalTitleValue = `Assign QTAG to ${p.asset_reference || p.product_name}`
    assign.dataset.modalTitleVerbatimValue = "true"
    assign.dataset.modalSizeValue = "modal-md"
    links.appendChild(assign)
  }
  return links
}
